import React, { useCallback, useEffect, useState } from 'react'
import DashboardContainer from 'components/smart/dashboard-container/DashboardContainer'
import { useNavigate } from 'react-router-dom'
import AdxTabsBadge from 'components/reusable/tab/AdxTabsBadge'
import AdxTabsPanel from 'components/reusable/tab/AdxTabsPanel'
import { Box, CircularProgress, Typography } from '@mui/material'
import { endOfMonth, format, startOfMonth } from 'date-fns'

import ClaimPendingTab from 'pages/approval-center/components/claim/ClaimPendingTab'
import ClaimUpcomingTab from 'pages/approval-center/components/claim/ClaimUpcomingTab'
import ClaimRespondedTab from 'pages/approval-center/components/claim/ClaimRespondedTab'
import { useQuery } from 'react-query'
import {
  getQueryApprovalReports,
  getRespondedReport,
  getClaimsPending,
} from 'services/api/modules/claim/approval-center/report'
import useApprovalFilterTabs from 'pages/approval-center/util/useApprovalFilterTabs'

const ClaimApprovalCenterOverview = ({ initialTab, module }) => {
  // change state from child to parent
  const [tabIndex, setTabIndex] = useState(initialTab)
  const { filteredTabs } = useApprovalFilterTabs()

  // Pagination state for pending claims
  const [pendingPage, setPendingPage] = useState(0)
  const [pendingRowsPerPage, setPendingRowsPerPage] = useState(5)
  const [upcomingPage, setUpcomingPage] = useState(0)
  const [upcomingRowsPerPage, setUpcomingRowsPerPage] = useState(5)
  const [respondedPage, setRespondedPage] = useState(0)
  const [respondedRowsPerPage, setRespondedRowsPerPage] = useState(5)
  const [respondedDateFrom, setRespondedDateFrom] = useState(
    format(startOfMonth(new Date()), 'yyyy-MM-dd')
  )
  const [respondedDateTo, setRespondedDateTo] = useState(
    format(endOfMonth(new Date()), 'yyyy-MM-dd')
  )

  // function to change route based on tabIndex
  const navigate = useNavigate()

  const changeRoute = useCallback(
    (index) => {
      if (filteredTabs?.length > 0) {
        navigate(`/app/approval-center/claim/${filteredTabs[index]?.link}`)
      }
    },
    [filteredTabs, navigate]
  )

  // useEffect
  useEffect(() => {
    changeRoute(tabIndex)
  }, [tabIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  // API call
  // current reports - using new paginated endpoint
  const {
    isLoading: currentIsLoading,
    data: currentRequestData,
    error: currentError,
  } = useQuery(['currentRequest', pendingPage + 1, pendingRowsPerPage], async () => {
    const { data } = await getClaimsPending(pendingPage + 1, pendingRowsPerPage, {
      sortBy: 'submitted_at',
      sortOrder: 'asc',
    })
    return data
  })

  const currentRequest = currentRequestData?.reports
  const currentPagination = currentRequestData?.pagination

  // upcoming reports
  const {
    isLoading: upcomingIsLoading,
    data: upcomingRequestData,
    error: upcomingError,
  } = useQuery(['upcomingRequest', upcomingPage + 1, upcomingRowsPerPage], async () => {
    const { data } = await getQueryApprovalReports(
      'upcoming',
      upcomingPage + 1,
      upcomingRowsPerPage,
      {
        sortBy: 'submitted_at',
        sortOrder: 'asc',
      }
    )
    return data
  })

  const upcomingRequest = upcomingRequestData?.reports || upcomingRequestData
  const upcomingPagination = upcomingRequestData?.pagination
  // responded reports
  const {
    isLoading: respondedIsLoading,
    data: respondedRequestData,
    error: respondedError,
  } = useQuery(
    ['respondedRequest', respondedPage + 1, respondedRowsPerPage, respondedDateFrom, respondedDateTo],
    async () => {
      const { data } = await getRespondedReport(respondedPage + 1, respondedRowsPerPage, {
        sortBy: 'updated_at',
        sortOrder: 'desc',
        dateFrom: respondedDateFrom,
        dateTo: respondedDateTo,
      })
      return data
    },
    {
      enabled:
        (!respondedDateFrom && !respondedDateTo) ||
        Boolean(respondedDateFrom && respondedDateTo && respondedDateFrom <= respondedDateTo),
    }
  )

  const respondedRequest = respondedRequestData?.reports || respondedRequestData
  const respondedPagination = respondedRequestData?.pagination

  const allLoading = () => {
    return [currentIsLoading, upcomingIsLoading, respondedIsLoading].some((item) => item === true)
  }

  // Pagination handlers
  const handlePendingPageChange = (event, newPage) => {
    setPendingPage(newPage)
  }

  const handlePendingRowsPerPageChange = (event) => {
    setPendingRowsPerPage(parseInt(event.target.value, 10))
    setPendingPage(0)
  }

  const handleUpcomingPageChange = (event, newPage) => {
    setUpcomingPage(newPage)
  }

  const handleUpcomingRowsPerPageChange = (event) => {
    setUpcomingRowsPerPage(parseInt(event.target.value, 10))
    setUpcomingPage(0)
  }

  const handleRespondedPageChange = (event, newPage) => {
    setRespondedPage(newPage)
  }

  const handleRespondedRowsPerPageChange = (event) => {
    setRespondedRowsPerPage(parseInt(event.target.value, 10))
    setRespondedPage(0)
  }

  const handleRespondedDateFromChange = (dateFrom) => {
    setRespondedDateFrom(dateFrom)
    setRespondedPage(0)
  }

  const handleRespondedDateToChange = (dateTo) => {
    setRespondedDateTo(dateTo)
    setRespondedPage(0)
  }

  return (
    <DashboardContainer
      headerTitle={
        <Typography fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
          {module}
        </Typography>
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          bgcolor: 'white.main',
          border: 1,
          borderColor: 'gray-line.main',
          borderRadius: '4.5px',
        }}
      >
        {allLoading() ? (
          <Box
            sx={{
              height: 'calc(100vh - 100px)',
              width: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress color="alternate" size={60} />
          </Box>
        ) : (
          <AdxTabsBadge
            scrollButtons={false}
            sxPanel={{
              bgcolor: 'transparent',
              boxSizing: 'border-box',
            }}
            setTabIndex={setTabIndex}
            initialValue={tabIndex}
          >
            <AdxTabsPanel label="Pending" notification={currentPagination?.total || currentRequest?.length}>
              <ClaimPendingTab
                pendingData={currentRequest}
                error={currentError}
                pagination={currentPagination}
                page={pendingPage}
                rowsPerPage={pendingRowsPerPage}
                onPageChange={handlePendingPageChange}
                onRowsPerPageChange={handlePendingRowsPerPageChange}
              />
            </AdxTabsPanel>
            <AdxTabsPanel label="Pending Pre-Approval" notification={upcomingPagination?.total || upcomingRequest?.length}>
              <ClaimUpcomingTab
                upcomingData={upcomingRequest}
                error={upcomingError}
                pagination={upcomingPagination}
                page={upcomingPage}
                rowsPerPage={upcomingRowsPerPage}
                onPageChange={handleUpcomingPageChange}
                onRowsPerPageChange={handleUpcomingRowsPerPageChange}
              />
            </AdxTabsPanel>
            <AdxTabsPanel label="Approved">
              <ClaimRespondedTab
                respondedData={respondedRequest}
                error={respondedError}
                pagination={respondedPagination}
                page={respondedPage}
                rowsPerPage={respondedRowsPerPage}
                onPageChange={handleRespondedPageChange}
                onRowsPerPageChange={handleRespondedRowsPerPageChange}
                dateFrom={respondedDateFrom}
                dateTo={respondedDateTo}
                onDateFromChange={handleRespondedDateFromChange}
                onDateToChange={handleRespondedDateToChange}
              />
            </AdxTabsPanel>
          </AdxTabsBadge>
        )}
      </Box>
    </DashboardContainer>
  )
}

export default ClaimApprovalCenterOverview
